import Link from "next/link";
import { ArrowUpRight } from "lucide-react";

import { cn } from "@/lib/utils";

export type HubCardItem = {
  title: string;
  description: string;
  href: string;
  image?: string;
  stat?: string;
};

const COLUMN_CLASS: Record<number, string> = {
  1: "",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

export const columnsForCount = (count: number): number => {
  if (count <= 2) return Math.max(count, 1);
  if (count % 4 === 0) return 4;
  if (count % 3 === 0) return 3;
  if (count % 2 === 0) return 2;
  return 3;
};

export const HubCard = ({ item }: { item: HubCardItem }) => (
  <Link
    href={item.href}
    prefetch={false}
    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E7ECF6] bg-white transition-all duration-200 hover:border-crayoBlue/40 hover:shadow-[0_6px_24px_-8px_rgba(77,127,237,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crayoBlue/40"
  >
    {item.image && (
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        draggable={false}
        className="h-[148px] w-full select-none border-b border-[#EFF2FF] bg-[#F7F7F7] object-cover"
      />
    )}
    <div className="flex flex-1 flex-col p-5">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-[17px] font-semibold leading-[1.25] tracking-tight text-gray-900 transition-colors group-hover:text-crayoBlue">
          {item.title}
        </h3>
        <ArrowUpRight
          className="mt-0.5 size-4 shrink-0 text-[#8B93A7] transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-crayoBlue"
          strokeWidth={2}
        />
      </div>
      <p className="mt-2 line-clamp-3 text-[13.5px] leading-[1.6] text-[#5C6678]">
        {item.description}
      </p>
      {item.stat && (
        <span className="mt-auto pt-4 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]">
          {item.stat}
        </span>
      )}
    </div>
  </Link>
);

export const HubGrid = ({ items, columns }: { items: HubCardItem[]; columns?: number }) => {
  if (!items.length) return null;

  const cols = COLUMN_CLASS[columns ?? columnsForCount(items.length)] ?? COLUMN_CLASS[3];

  return (
    <ul className={cn("grid w-full grid-cols-1 gap-4 sm:gap-5", cols)}>
      {items.map((item) => (
        <li key={item.href} className="h-full">
          <HubCard item={item} />
        </li>
      ))}
    </ul>
  );
};

export const HubHero = ({
  title,
  intro,
  eyebrow,
}: {
  title: string;
  intro: string;
  eyebrow?: string;
}) => (
  <section className="border-b border-[#E7ECF6] bg-white">
    <div className="mx-auto w-full max-w-screen-xl px-5 pb-12 pt-8 text-center sm:px-6 md:px-12 md:pb-16 md:pt-12 lg:px-20">
      <div className="mx-auto flex max-w-[760px] flex-col items-center gap-5">
        {eyebrow && (
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8B93A7]">
            {eyebrow}
          </span>
        )}
        <h1 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl lg:text-[56px] lg:leading-[1.05]">
          {title}
        </h1>
        <p className="max-w-[620px] text-[15px] leading-[1.6] text-[#5C6678] sm:text-[17px]">
          {intro}
        </p>
      </div>
    </div>
  </section>
);

export const HubSection = ({
  title,
  blurb,
  items,
  columns,
}: {
  title: string;
  blurb?: string;
  items: HubCardItem[];
  columns?: number;
}) => {
  if (!items.length) return null;

  return (
    <section className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <h2 className="text-[24px] font-semibold tracking-tight text-gray-900 md:text-[28px]">
          {title}
        </h2>
        {blurb && <p className="text-[14.5px] leading-[1.6] text-[#5C6678]">{blurb}</p>}
      </div>
      <HubGrid items={items} columns={columns} />
    </section>
  );
};
